import React, { useRef } from 'react';
import styled from 'styled-components';
import { Carousel } from 'antd';
import useTranslation from '@src/hooks/useTranslation';

import { CardLearningPathCourse } from '@src/components/common/icons/CardLearningPathCourse';
import { NextArrowIcon } from '@src/components/common/icons/NextArrowIcon';
import { PrevArrowIcon } from '@src/components/common/icons/PrevArrowIcon';
import { CardLearningPathCertificate } from '@src/components/common/icons/CardLearningPathCertificate';

const Content = styled.div`
	max-width: 100vw;
	width: 100%;
	overflow: hidden;
	word-break: break-all;
	cursor: pointer;
	:hover {
		.title {
			color: ${props => props.theme.colors.primary};
		}
	}
`;

const ImageWrap = styled.div`
	position: relative;
`;

const StyledCarousel = styled(Carousel)`
	& .slick-dots .slick-active {
		width: 6px !important;
		height: 6px !important;
	};
	& .slick-dots .slick-active button {
		border-radius: 100px !important;
		width: 6px !important;
		 height: 6px !important;
	};
	& .slick-dots {
		width: 6px !important;
		height: 6px !important;
		margin: unset !important;
    left: 50% !important;
	};
	& .slick-dots button {
		border-radius: 100px !important;
		width: 6px !important;
		 height: 6px !important;
	};
	$
`;

const InfoContent = styled.div`
	background: #FFFFFF;
	padding: 8px;
`;

const Title = styled.div`
	font-weight: 500;
	font-size: 18px;
	color: #363636;
	margin-bottom: 8px;
`;

const Desc = styled.div`
	font-size: 12px;
	color: #797979;
`;

const AttrInfoContent = styled.div`
	background: #FFFFFF;
	border-top: 1px solid #E9E9E9;
	border-bottom: 1px solid #E9E9E9;
	padding: 9px 11px;
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const AttrInfoWrap = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
`;

const Text = styled.div`
	margin-left: 8px;
	font-size: 12px;
	color: #797979;
`;

const NextBtn = styled.div`
	position: absolute;
	top: calc(50% - 12px);
	right: 0px;
	z-index: 1;
`;

const PrevBtn = styled.div`
	position: absolute;
	top: calc(50% - 12px);
	left: 0px;
	z-index: 1;
`;

const Divider = styled.div`
	margin: 0 12px;
	height: 12px;
	border-right: 1px solid #E9E9E9;
`;

interface ILearningPathCard {
	photos: {
		id: string;
		link: string;
		alt?: string;
	}[],
	title: string;
	desc: string;
	onClick: () => void;
	courseNumber: number;
	hasCertificate: number;
}

const LearningPathCard = ({
	photos,
	title,
	desc,
	onClick,
	courseNumber,
	hasCertificate,
}: ILearningPathCard): JSX.Element => {
	const { t } = useTranslation();
	const slider = useRef(null);
	const nextButton = e => {
		e.stopPropagation();
		slider.current.next();
	};
	const prevButton = e => {
		e.stopPropagation();
		slider.current.prev();
	};

	return (
		<Content onClick={onClick}>
			<ImageWrap>
				{
					photos.length > 1 && (
						<PrevBtn onClick={prevButton}>
							<PrevArrowIcon />
						</PrevBtn>
					)
				}
				<StyledCarousel ref={slider} swipeToSlide draggable>
					{
						photos.map(photo => (
							<img key={photo.id} src={photo.link} alt={photo.alt} />
						))
					}
				</StyledCarousel>
				{
					photos.length > 1 && (
						<NextBtn onClick={nextButton}>
							<NextArrowIcon />
						</NextBtn>
					)
				}
			</ImageWrap>
			<InfoContent>
				<Title className="title">{title}</Title>
				<Desc>{desc}</Desc>
			</InfoContent>
			<AttrInfoContent>
				<AttrInfoWrap>
					<CardLearningPathCourse />
					<Text>{t('training:learning:path:card_course_number', { courseNumber })}</Text>
				</AttrInfoWrap>
				{
					hasCertificate && (
						<>
							<Divider />
							<AttrInfoWrap>
								<CardLearningPathCertificate />
								<Text>{t('training:learning:path:certificate')}</Text>
							</AttrInfoWrap>
						</>
					)
				}
			</AttrInfoContent>
		</Content>
	);
};

export default LearningPathCard;
